import React, { useState, useRef, useEffect } from 'react';
import { aiApi, ticketsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Mic, 
  MicOff, 
  Phone, 
  PhoneOff,
  Bot,
  User,
  Loader2,
  Volume2,
  VolumeX,
  AlertCircle,
  CheckCircle,
  Ticket
} from 'lucide-react';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

export default function VoiceCallWidget() {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [conversation, setConversation] = useState([
    {
      role: 'assistant',
      content: 'Benvenuto al servizio di assistenza tecnica. Clicca "Inizia Chiamata" per parlare con REBECCA. Descrivi il tuo problema e ti aiuteremo a risolverlo.',
      timestamp: new Date()
    }
  ]);
  const [error, setError] = useState('');
  const [ticketCreated, setTicketCreated] = useState(null);
  
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation]);

  // Initialize Speech Recognition
  const initSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError('Il tuo browser non supporta il riconoscimento vocale. Usa Chrome o Edge.');
      return null;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'it-IT';

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      setTranscript(finalTranscript || interimTranscript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        setError('Permesso microfono negato. Consenti l\'accesso al microfono nelle impostazioni del browser.');
      }
    };

    recognition.onend = () => {
      if (isRecording && isCallActive) {
        try {
          recognition.start();
        } catch (e) {
          console.log('Recognition already started');
        }
      }
    };

    return recognition;
  };

  // Start call
  const startCall = async () => {
    setError('');
    setIsCallActive(true);
    
    setConversation(prev => [...prev, {
      role: 'assistant',
      content: 'Ciao! Sono Rebecca, la tua assistente virtuale. Descrivi il tuo problema e clicca "Invia" quando hai finito.',
      timestamp: new Date()
    }]);
    
    // Play short welcome (faster response)
    speak('Ciao! Sono Rebecca. Descrivi il tuo problema.');
  };

  // End call
  const endCall = async () => {
    stopRecording();
    stopSpeaking();
    setIsCallActive(false);
    setIsSpeaking(false);
    
    // Create ticket if there was a conversation
    const userMessages = conversation.filter(m => m.role === 'user');
    if (userMessages.length > 0) {
      try {
        const description = userMessages.map(m => m.content).join('\n');
        const aiMessages = conversation.filter(m => m.role === 'assistant' && m.content.includes('Diagnosi'));
        const aiDiagnosis = aiMessages.length > 0 ? aiMessages[aiMessages.length - 1].content : '';
        
        const ticket = await ticketsApi.create({
          title: `Chiamata vocale: ${userMessages[0].content.substring(0, 50)}...`,
          description: description,
          priority: 'medium',
          category: 'general',
          caller_name: 'Utente Web'
        });
        
        setTicketCreated(ticket.data);
        
        setConversation(prev => [...prev, {
          role: 'system',
          content: `Ticket #${ticket.data.id.slice(-8)} creato con successo! Un tecnico ti contatterà presto.`,
          timestamp: new Date()
        }]);
        
        speak('Ticket creato! Un tecnico ti contatterà presto. A presto!');
      } catch (err) {
        console.error('Failed to create ticket:', err);
      }
    }
    
    setConversation(prev => [...prev, {
      role: 'assistant',
      content: 'Chiamata terminata. Grazie per aver contattato il supporto!',
      timestamp: new Date()
    }]);
  };

  // Start recording
  const startRecording = () => {
    if (!recognitionRef.current) {
      recognitionRef.current = initSpeechRecognition();
    }
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
        setTranscript('');
      } catch (e) {
        console.error('Failed to start recognition:', e);
      }
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsRecording(false);
  };

  // Send message to AI
  const sendMessage = async () => {
    if (!transcript.trim()) return;
    
    const userMessage = transcript.trim();
    setTranscript('');
    stopRecording();
    
    // Add user message to conversation
    setConversation(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    }]);
    
    setIsProcessing(true);
    
    try {
      const response = await aiApi.chat(userMessage, null, 'Chiamata vocale di assistenza tecnica');
      const aiResponse = response.data.response;
      
      // Add AI response to conversation
      setConversation(prev => [...prev, {
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date()
      }]);
      
      // Speak the response
      speak(aiResponse);
      
    } catch (err) {
      console.error('AI chat error:', err);
      const errorMsg = 'Mi dispiace, si è verificato un errore. Per favore riprova o contatta il supporto direttamente.';
      setConversation(prev => [...prev, {
        role: 'assistant',
        content: errorMsg,
        timestamp: new Date()
      }]);
      speak(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  // Text-to-speech using OpenAI TTS (REBECCA voice)
  const speak = async (text) => {
    setIsSpeaking(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ai/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text: text.slice(0, 500) })
      });
      const data = await res.json();
      if (data.audio) {
        if (audioRef.current) audioRef.current.pause();
        const audio = new Audio(data.audio);
        audioRef.current = audio;
        audio.onended = () => setIsSpeaking(false);
        audio.onerror = () => setIsSpeaking(false);
        audio.play();
      } else {
        setIsSpeaking(false);
      }
    } catch (e) {
      console.error('TTS error:', e);
      setIsSpeaking(false);
    }
  };

  // Stop speaking
  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsSpeaking(false);
  };

  return (
    <div className="space-y-6" data-testid="voice-call-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Assistenza Vocale
        </h1>
        <p className="text-[#71717A] mt-1">Parla direttamente con REBECCA</p>
      </div>

      {/* Call Status */}
      <div className="flex justify-center">
        {isCallActive ? (
          <Badge className="bg-green-100 text-green-700 border-green-300 px-4 py-2 text-sm">
            <span className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse"></span>
            Chiamata Attiva
          </Badge>
        ) : (
          <Badge variant="secondary" className="px-4 py-2 text-sm">
            <Phone className="w-4 h-4 mr-2" />
            Non connesso
          </Badge>
        )}
      </div>

      {/* Main Card */}
      <Card className="max-w-2xl mx-auto border-2 border-[#E4E4E7] shadow-lg">
        <CardHeader className="pb-2 border-b border-[#E4E4E7]">
          <CardTitle className="flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isCallActive ? 'bg-[#FDE047] ai-pulse' : 'bg-[#E4E4E7]'
            }`}>
              <Bot className={`w-6 h-6 ${isCallActive ? 'text-[#0F172A]' : 'text-[#71717A]'}`} />
            </div>
            <div>
              <p className="font-medium">REBECCA</p>
              <p className="text-sm text-[#71717A] font-normal">
                {isSpeaking ? 'Sta parlando...' : isProcessing ? 'Elaborazione...' : 'In attesa'}
              </p>
            </div>
            {isSpeaking && (
              <Button
                variant="ghost"
                size="sm"
                onClick={stopSpeaking}
                className="ml-auto"
              >
                <VolumeX className="w-4 h-4" />
              </Button>
            )}
          </CardTitle>
        </CardHeader>

        <CardContent className="p-0">
          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border-b border-red-200 flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Conversation */}
          <ScrollArea className="h-[350px] p-4" ref={scrollRef}>
            <div className="space-y-4">
              {conversation.map((msg, idx) => (
                <div 
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`voice-message-${idx}`}
                >
                  {msg.role === 'system' ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 max-w-[90%]">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <p className="text-sm text-green-800">{msg.content}</p>
                    </div>
                  ) : (
                    <div className={`flex gap-2 max-w-[80%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.role === 'user' ? 'bg-[#002FA7]' : 'bg-[#FDE047]'
                      }`}>
                        {msg.role === 'user' ? (
                          <User className="w-4 h-4 text-white" />
                        ) : (
                          <Bot className="w-4 h-4 text-[#0F172A]" />
                        )}
                      </div>
                      <div className={`p-3 rounded-lg ${
                        msg.role === 'user' 
                          ? 'bg-[#002FA7] text-white' 
                          : 'bg-[#F4F4F5] text-[#0F172A]'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-xs mt-1 ${
                          msg.role === 'user' ? 'text-blue-200' : 'text-[#71717A]'
                        }`}>
                          {msg.timestamp.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Processing indicator */}
              {isProcessing && (
                <div className="flex justify-start">
                  <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#FDE047] flex items-center justify-center">
                      <Bot className="w-4 h-4 text-[#0F172A]" />
                    </div>
                    <div className="p-3 bg-[#F4F4F5] rounded-lg">
                      <Loader2 className="w-5 h-5 animate-spin text-[#002FA7]" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Transcript Preview */}
          {isRecording && transcript && (
            <div className="px-4 py-2 bg-blue-50 border-t border-blue-100">
              <p className="text-sm text-[#002FA7]">
                <Mic className="w-4 h-4 inline mr-2 animate-pulse" />
                {transcript}
              </p>
            </div>
          )}

          {/* Controls */}
          <div className="p-4 border-t border-[#E4E4E7] bg-white">
            {!isCallActive ? (
              <Button
                onClick={startCall}
                className="w-full bg-[#16A34A] hover:bg-[#16A34A]/90 text-white h-14 text-lg"
                data-testid="start-call-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                Inizia Chiamata
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-3">
                  {/* Mic Button */}
                  <Button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isProcessing || isSpeaking}
                    className={`flex-1 h-12 ${
                      isRecording 
                        ? 'bg-red-500 hover:bg-red-600' 
                        : 'bg-[#002FA7] hover:bg-[#002FA7]/90'
                    }`}
                    data-testid="mic-btn"
                  >
                    {isRecording ? (
                      <>
                        <MicOff className="w-5 h-5 mr-2" />
                        Stop Registrazione
                      </>
                    ) : (
                      <>
                        <Mic className="w-5 h-5 mr-2" />
                        Parla
                      </>
                    )}
                  </Button>

                  {/* Send Button */}
                  <Button
                    onClick={sendMessage}
                    disabled={!transcript.trim() || isProcessing}
                    className="flex-1 h-12 bg-[#FDE047] text-[#0F172A] hover:bg-[#FACC15]"
                    data-testid="send-voice-btn"
                  >
                    {isProcessing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      'Invia'
                    )}
                  </Button>
                </div>

                {/* End Call Button */}
                <Button
                  onClick={endCall}
                  variant="outline"
                  className="w-full h-10 border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="end-call-btn"
                >
                  <PhoneOff className="w-4 h-4 mr-2" />
                  Termina Chiamata
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Ticket Created */}
      {ticketCreated && (
        <Card className="max-w-2xl mx-auto border-green-200 bg-green-50">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-medium text-green-800">Ticket Creato!</p>
              <p className="text-sm text-green-700">
                ID: #{ticketCreated.id?.slice(-8)} - Un tecnico ti contatterà presto.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto border-green-300 text-green-700 hover:bg-green-100"
              onClick={() => window.location.href = `/tickets/${ticketCreated.id}`}
            >
              <Ticket className="w-4 h-4 mr-1" />
              Vedi Ticket
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="max-w-2xl mx-auto border border-[#E4E4E7]">
        <CardContent className="p-4">
          <h3 className="font-medium mb-2">Come funziona:</h3>
          <ol className="text-sm text-[#71717A] space-y-1 list-decimal list-inside">
            <li>Clicca <strong>"Inizia Chiamata"</strong> per connetterti a REBECCA</li>
            <li>Clicca <strong>"Parla"</strong> e descrivi il tuo problema</li>
            <li>Clicca <strong>"Invia"</strong> quando hai finito di parlare</li>
            <li>REBECCA analizzerà e risponderà vocalmente</li>
            <li>Al termine, verrà creato un ticket automaticamente</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
